import {
  CSSProperties,
} from 'react'

import {createUseStyles} from 'react-jss'
import merge from 'deepmerge'

import {
  ILayout,
  ILayout_Cell,
  ILayout_Row,
  ILayout_Cell_Config,
  ILayout_Row_Config,
  ILayout_Cell_Props_Text,
  ILayout_Cell_Props_Image,
} from '../types/layout'

import {
  IData_MagicTag_Instance,
  IData_MagicTag_Render_Values,
} from '../types/schema/magic_tag'

import {
  LAYOUT_HORIZONTAL_ALIGN_LEFT,
  LAYOUT_HORIZONTAL_ALIGN_CENTER,
  LAYOUT_HORIZONTAL_ALIGN_RIGHT,
  LAYOUT_HORIZONTAL_ALIGN_DEFAULT,
  LAYOUT_VERTICAL_ALIGN_TOP,
  LAYOUT_VERTICAL_ALIGN_MIDDLE,
  LAYOUT_VERTICAL_ALIGN_BOTTOM,
  LAYOUT_VERTICAL_ALIGN_DEFAULT,
  LAYOUT_CELL_TYPE_IMAGE,
  LAYOUT_CELL_TYPE_TEXT,
  LAYOUT_MEDIA_QUERY_WIDTH,
} from '../constants'

import {
  getMarkdownFromRaw,
  getMagicTags as getMagicTagsText,
} from '../texteditor/utils'

import {
  removeEmptyFields,
} from '../utils/object'

import {
  getNumber,
} from '../utils/number'

export const isNodeRow = (node: ILayout_Cell | ILayout_Row): node is ILayout_Row => {
  if('cells' in node) return true
  else return false
}

export const spacing = (count = 1) => count * 8

export const getHorizontalAlign = (align?: string): string => {
  if(align == LAYOUT_HORIZONTAL_ALIGN_LEFT) return 'flex-start'
  else if(align == LAYOUT_HORIZONTAL_ALIGN_CENTER) return 'center'
  else if(align == LAYOUT_HORIZONTAL_ALIGN_RIGHT) return 'flex-end'
  else return getHorizontalAlign(LAYOUT_HORIZONTAL_ALIGN_DEFAULT)
}

export const getVerticalAlign = (align?: string): string => {
  if(align == LAYOUT_VERTICAL_ALIGN_TOP) return 'flex-start'
  else if(align == LAYOUT_VERTICAL_ALIGN_MIDDLE) return 'center'
  else if(align == LAYOUT_VERTICAL_ALIGN_BOTTOM) return 'flex-end'
  else return getVerticalAlign(LAYOUT_VERTICAL_ALIGN_DEFAULT)
}

export interface ILayoutCSS_Props_Cell {
  config: ILayout_Cell_Config,
  editorMode?: boolean,
  percentWidth?: number,
}

export interface ILayoutCSS_Props_Row {
  config: ILayout_Row_Config,
  editorMode?: boolean,
  topLevel?: boolean,
}

export const useCellTableStyles = createUseStyles({
  td: ({config = {}, percentWidth = 0}: ILayoutCSS_Props_Cell) => ({
    padding: spacing(typeof(config.padding) == 'number' ? config.padding : 1),
    horizontalAlign: config.horizontal_align || LAYOUT_HORIZONTAL_ALIGN_DEFAULT,
    verticalAlign: config.vertical_align || LAYOUT_VERTICAL_ALIGN_DEFAULT,
    textAlign: config.horizontal_align || LAYOUT_HORIZONTAL_ALIGN_DEFAULT,
    width: `${percentWidth}%`,
  }),
})

export const useCellStyles = createUseStyles({
  cell: ({config: {width} = {width:0}, percentWidth, editorMode}: ILayoutCSS_Props_Cell) => ({
    width: `${percentWidth}%`,
    flexGrow: width || 1,
    flexBasis: 0,
    minHeight: editorMode ? '50px' : '',
    marginLeft: editorMode ? spacing(0.5) : 0,
    marginRight: editorMode ? spacing(0.5) : 0,
    backgroundColor: '#fff',
    position: 'relative',
  }),
  content: ({config: {padding, vertical_align, horizontal_align} = {padding: 0, vertical_align: 'middle', horizontal_align: 'center'}, editorMode}: ILayoutCSS_Props_Cell) => ({
    padding: spacing(typeof(padding) == 'number' ? padding : 1),
    height: '100%',
    display: 'flex',
    alignItems: getVerticalAlign(vertical_align),
    justifyContent: getHorizontalAlign(horizontal_align),
    textAlign: horizontal_align || LAYOUT_HORIZONTAL_ALIGN_DEFAULT,
    cursor: editorMode ? 'pointer' : '',
  }),
  locked: {
    position: 'absolute',
    left: '0px',
    top: '0px',
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(255,255,255,0.5)',
    zIndex: 100,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#999',
  },
  [`@media screen and (max-width: ${LAYOUT_MEDIA_QUERY_WIDTH})`]: ({config: {width}, percentWidth}: ILayoutCSS_Props_Cell) => ({
    cell: {
      width: `100% !important`,
      flexGrow: 1,
    }
  })
})

export const useRowTableStyles = createUseStyles({
  row: ({config: {margin} = {margin: 0}}: ILayoutCSS_Props_Row) => ({
    marginTop: spacing(getNumber(margin, 0)),
    marginBottom: spacing(getNumber(margin, 0)),
  }),
  table: {
    border: 'none',
  }
})

export const useRowStyles = createUseStyles({
  row: ({config: {width, margin} = {width:0,margin:0}, topLevel, editorMode}: ILayoutCSS_Props_Row) => {
    let marginTop = 0
    let marginLeft = 0
    let marginRight = 0
    let marginBottom = 0

    if(editorMode && topLevel) {
      marginLeft = spacing(0.5)
      marginRight = spacing(0.5)
      marginBottom = 20
    }
    else if(!editorMode) {
      marginTop = spacing(getNumber(margin, 0))
      marginBottom = spacing(getNumber(margin, 0))
    }

    return {
      display: 'flex',
      flexDirection: 'row',
      flexGrow: width,
      flexBasis: 0,
      marginTop,
      marginLeft,
      marginRight,
      marginBottom,
    }
  },
  cellContainer: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  margin: ({config: {margin} = {margin: 0}}: ILayoutCSS_Props_Row) => ({
    width: '100%',
    flexGrow: 0,
    minHeight: spacing(getNumber(margin, 0)),
    border: getNumber(margin, 0) > 0 ? '1px dashed #999' : '',
    backgroundColor: getNumber(margin, 0) > 0 ? '#f5f5f5' : '',
  }),
  cells: {
    flexGrow: 1,
    height: '100%',
  },
  [`@media screen and (min-width: ${LAYOUT_MEDIA_QUERY_WIDTH})`]: {
    cells: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'stretch',
    }
  }
})

export const getCells = (
  value: ILayout,
): ILayout_Cell[] => {
  const getRowCells = (row: ILayout_Row): ILayout_Cell[] => {
    return row.cells.reduce<ILayout_Cell[]>((all, cell) => {
      if(isNodeRow(cell)) {
        return all.concat(getRowCells(cell))
      }
      else {
        return all.concat([cell])
      }
    }, [])
  }
  return value.rows.reduce<ILayout_Cell[]>((all, row) => {
    return all.concat(getRowCells(row))
  }, [])
}

export const getMagicTags = (
  value: ILayout,
): IData_MagicTag_Instance[] => {

  const cells = getCells(value)
  return cells.reduce<IData_MagicTag_Instance[]>((all, cell) => {
    if(cell.type == LAYOUT_CELL_TYPE_TEXT) {
      const data = cell.data as ILayout_Cell_Props_Text
      return all.concat(getMagicTagsText(data.text))
    }
    else if(cell.type == LAYOUT_CELL_TYPE_IMAGE) {
      const data = cell.data as ILayout_Cell_Props_Image
      if(!data.link || !data.link.url) return all
      return all.concat(getMagicTagsText(data.link.url))
    }
    else {
      return all
    }
  }, [])

}

export const getMagicTagDefaults = (
  value: ILayout,
): IData_MagicTag_Render_Values => {
  return getMagicTags(value).reduce<IData_MagicTag_Render_Values>((all, tag) => {
    const group = all[tag.type] || {}
    if(!group[tag.field]) {
      group[tag.field] = tag.default || ''
    }
    all[tag.type] = group
    return all
  }, {})
}

export const stripRenderValues = (values: IData_MagicTag_Render_Values): IData_MagicTag_Render_Values => {
  return Object
    .keys(values)
    .reduce<IData_MagicTag_Render_Values>((all, field) => {
      all[field] = removeEmptyFields(values[field])
      return all
    }, {})
}

// given a layout and some values - merge the defaults
export const mergeRenderValues = (
  layout: ILayout,
  values: IData_MagicTag_Render_Values,
): IData_MagicTag_Render_Values => {
  const defaultValues = getMagicTagDefaults(layout)
  const actualValues = stripRenderValues(values)
  return merge(defaultValues, actualValues)
}

export const toString = (
  layout: ILayout,
): string => {
  const textCells = getCells(layout).filter(cell => cell.type == LAYOUT_CELL_TYPE_TEXT) as ILayout_Cell<ILayout_Cell_Props_Text>[]
  return textCells.map(cell => getMarkdownFromRaw(cell.data.text)).join(' ')
}

export function extractSetValue<T = any>(set: Set<T>): T{
  // extract the first value of the set and return it
  const iterator = set.values()
  return iterator.next().value
}